/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createAsyncThunk } from "@reduxjs/toolkit";

import { Log } from "../../model/logging";
import { PostSigninInteractor } from "../../model/interactor";
import type { UnityAppDispatch } from "../UnityAppStore";
import { channelLineupRefreshed } from "./profilesSlice";

const TAG: string = "profileScopedData";

/**
 * What one fetch in {@link PROFILE_SCOPED_FETCHES} is handed.
 *
 * `postSigninInteractor` is passed in rather than constructed per entry so the
 * startup sequence can share the instance it already holds; an entry that
 * needs a different interactor is free to build its own.
 */
export interface TProfileScopedFetchContext {
  dispatch: UnityAppDispatch;
  postSigninInteractor: PostSigninInteractor;
}

/** One service whose response belongs to a single profile. */
export interface TProfileScopedFetch {
  /** Stable identifier, used in logs to name the entry that failed. */
  id: string;
  /**
   * Drops whatever this entry's data is cached in, so {@link run} cannot be
   * answered from the previous profile's copy. Runs immediately before it.
   *
   * Optional, but omit it only when the fetch genuinely has no cache in front
   * of it — a cached service without an `invalidate` silently stops issuing
   * its call on a profile change, which looks exactly like the entry working.
   */
  invalidate?: (context: TProfileScopedFetchContext) => Promise<void>;
  run: (context: TProfileScopedFetchContext) => Promise<void>;
}

/**
 * The data that belongs to the active profile and is therefore wrong the
 * moment a different profile becomes active.
 *
 * **This is the list to extend.** Adding an entry here is all it takes for a
 * service to be re-requested on every profile selection and every profile
 * switch, and to take its place in the post sign-in startup sequence — both
 * paths run this same list, in this order, so the two cannot drift apart.
 *
 * Rules for an entry:
 *
 *  - Order matters. Entries run sequentially, top to bottom, because the
 *    startup sequence's call order is a product requirement and this list is
 *    spliced into it at Steps 5-6.
 *  - Failure is contained. Each entry is awaited inside its own try/catch, so
 *    one dead service degrades its own feature and nothing else — the same
 *    contract every other startup step has.
 *  - Caches are declared. If the service is served from a local cache, the
 *    entry says how to drop it in `invalidate`; the list is the reason a call
 *    is made, so an entry that quietly hits a cache defeats the point of it.
 *  - No token handling. The profile-scoped domain token is already minted and
 *    the active context already switched before any of this runs, so an entry
 *    just issues its call and lets `BaseInteractor` resolve the credential.
 */
export const PROFILE_SCOPED_FETCHES: TProfileScopedFetch[] = [
  {
    // Station policies ride along: the domain-lib ChannelsService fetches them
    // alongside the channel list and merges the two before returning.
    id: "stationPolicies+channels",
    // ChannelsService caches the line-up for six hours keyed on time alone, so
    // without this the fetch below is answered from the profile the user just
    // left and no request reaches the service.
    invalidate: async (context: TProfileScopedFetchContext): Promise<void> => {
      await context.postSigninInteractor.clearChannelsCache();
    },
    run: async (context: TProfileScopedFetchContext): Promise<void> => {
      await context.postSigninInteractor.fetchChannels();
      context.dispatch(channelLineupRefreshed());
    }
  }
];

/**
 * Runs every entry in {@link PROFILE_SCOPED_FETCHES}, in order, each one
 * isolated from the others' failures.
 *
 * Shared by the startup sequence and {@link refreshProfileScopedData} so there
 * is exactly one definition of "what a profile owns".
 */
export async function runProfileScopedFetches(
  context: TProfileScopedFetchContext
): Promise<void> {
  for (const entry of PROFILE_SCOPED_FETCHES) {
    try {
      Log.d(TAG, "runProfileScopedFetches() " + entry.id);
      if (entry.invalidate !== undefined) {
        await entry.invalidate(context);
      }
      await entry.run(context);
    } catch (error: unknown) {
      Log.e(TAG, "runProfileScopedFetches() " + entry.id + " failed", error);
    }
  }
}

/**
 * Re-requests the profile-scoped data set after the active profile changed.
 *
 * Dispatched from the `activateProfile.fulfilled` listener, but only for a
 * switch made once startup has finished. During startup the sequence runs the
 * same list itself at Steps 5-6, and dispatching this alongside it would put
 * every one of these calls on the wire twice.
 *
 * Always fulfils: the entries report their own failures and a half-refreshed
 * profile is still a usable one.
 */
export const refreshProfileScopedData = createAsyncThunk<void, void>(
  "profiles/refreshProfileScopedData",
  async (_: void, { dispatch }): Promise<void> => {
    Log.d(TAG, "refreshProfileScopedData() active profile changed");
    await runProfileScopedFetches({
      dispatch: dispatch as UnityAppDispatch,
      postSigninInteractor: new PostSigninInteractor()
    });
    Log.d(TAG, "refreshProfileScopedData() complete");
  }
);
